import type { IEmailTo, TGetCommunicationTemplates } from "@api";
import { CommunicationTemplateType } from "@bbj-types/communication/enum";
import type { IVariable, ObjectId } from "@bbj-types/utils";
import { UPLOAD_FILE_FIELD_BUTTON_HEIGHT } from "@inputs/file";
import AttachmentOutlinedIcon from "@mui/icons-material/AttachmentOutlined";
import { Stack, type Theme, Typography, useMediaQuery } from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import type { Control, UseFormSetValue } from "react-hook-form";
import { AttachmentFilesButton } from "./attachment";
import { CcInput } from "./carbonCopy";
import { EmailContent } from "./content";
import { Emails } from "./emails";
import { MenuTemplate } from "./menuTemplateMail";
import type { TEmailForm } from "./resolver";
import { ObjectInput } from "./subject";

export interface IEmailFormTemplate {
  _id: ObjectId;
  content: string;
  name: string;
  subject?: string;
  type: CommunicationTemplateType.EMAIL;
  organisation: string;
}

export const isIEmailFormTemplate = (template: TGetCommunicationTemplates[number]): template is IEmailFormTemplate =>
  template.type === CommunicationTemplateType.EMAIL;

interface IEmailFormProps {
  emails: readonly IEmailTo[];
  control: Control<TEmailForm, void>;
  setValue: UseFormSetValue<TEmailForm>;
  templates: IEmailFormTemplate[];
  variables: IVariable[];
  setAttachmentsLoading: Dispatch<SetStateAction<boolean>>;
  candidateId?: ObjectId;
  emailsLimit?: number;
  disableEmails?: boolean;
  displayCc?: boolean;
  contentMaxHeight?: number;
}

export const EmailForm = ({
  emails,
  control,
  templates,
  setValue,
  variables,
  setAttachmentsLoading,
  candidateId,
  emailsLimit,
  disableEmails,
  displayCc,
  contentMaxHeight,
}: IEmailFormProps) => {
  const tablet = useMediaQuery((theme: Theme) => theme.breakpoints.up("tablet"));

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <MenuTemplate control={control} templates={templates} setValue={setValue} />
      <Stack
        direction={tablet ? "row" : "column"}
        sx={{
          gap: tablet ? 0 : 1,
          alignItems: tablet ? "center" : "start",
        }}
      >
        <Typography sx={{ minWidth: "7%" }}>À * :</Typography>
        <Emails
          label="Email"
          control={control}
          emails={emails}
          emailsLimit={emailsLimit}
          disableEmails={disableEmails}
        />
      </Stack>
      <CcInput control={control} displayCc={displayCc} />
      <ObjectInput control={control} variables={variables} />
      <Stack
        direction="row"
        sx={{
          alignItems: "flex-start",
          justifyContent: tablet ? "start" : "space-between",
        }}
      >
        <Stack
          sx={{ width: "7%", height: UPLOAD_FILE_FIELD_BUTTON_HEIGHT, alignItems: "center", justifyContent: "center" }}
        >
          <AttachmentOutlinedIcon color="secondary" />
        </Stack>
        <AttachmentFilesButton
          candidateId={candidateId}
          control={control}
          setAttachmentsLoading={setAttachmentsLoading}
        />
      </Stack>
      <EmailContent contentMaxHeight={contentMaxHeight} control={control} variables={variables} />
    </Stack>
  );
};
